import React, { useCallback, useMemo, useState } from "react";
import TodoForm from "./TodoForm";
import Todo, { type DashboardTodo } from "./Todo";
import { CardFooter, Badge } from "reactstrap";
import PerfectScrollbar from "react-perfect-scrollbar";

const DASHBOARD_DEFAULT_TODOS = [
  {
    id: "dash-task-1",
    text: "Review Q4 inventory and restock alerts",
    complete: false
  },
  {
    id: "dash-task-2",
    text: "Publish weekend promotion banner",
    complete: false
  },
  {
    id: "dash-task-3",
    text: "Sync CRM leads with sales pipeline",
    complete: true
  },
  {
    id: "dash-task-4",
    text: "Approve vendor invoices — batch #412",
    complete: false
  },
  {
    id: "dash-task-5",
    text: "Update shipping rates for EU zone",
    complete: false
  },
  {
    id: "dash-task-6",
    text: "Schedule vendor onboarding call — Acme Co.",
    complete: false
  },
  {
    id: "dash-task-7",
    text: "Audit return labels and refund queue",
    complete: true
  },
  {
    id: "dash-task-8",
    text: "Refresh homepage hero copy and CTA links",
    complete: false
  },
  {
    id: "dash-task-9",
    text: "Compile weekly KPI snapshot for leadership",
    complete: false
  },
  {
    id: "dash-task-10",
    text: "Test checkout flow on Safari / mobile",
    complete: false
  },
  {
    id: "dash-task-11",
    text: "Reply to escalated support tickets (priority)",
    complete: false
  },
  {
    id: "dash-task-12",
    text: "Back up product image assets to CDN folder",
    complete: true
  },
  {
    id: "dash-task-13",
    text: "Confirm Black Friday coupon codes in admin",
    complete: false
  },
  {
    id: "dash-task-14",
    text: "Review analytics goals and conversion events",
    complete: false
  },
  {
    id: "dash-task-15",
    text: "Update team wiki — deployment runbook",
    complete: false
  }
];

const SCROLL_OPTIONS = { suppressScrollX: true, wheelPropagation: false };

type TodoFilter = "all" | "active" | "complete";

export default function TodoList() {
  const [todos, setTodos] = useState<DashboardTodo[]>(DASHBOARD_DEFAULT_TODOS);
  const [todoToShow, setTodoToShow] = useState<TodoFilter>("all");
  const [toggleAllComplete, setToggleAllComplete] = useState(true);

  const addTodo = useCallback((todo: DashboardTodo) => {
    setTodos(prev => [todo, ...prev]);
  }, []);

  const toggleComplete = useCallback((id: string) => {
    setTodos(prev =>
      prev.map(todo =>
        todo.id === id ? { ...todo, complete: !todo.complete } : todo
      )
    );
  }, []);

  const updateTodoToShow = useCallback((s: TodoFilter) => {
    setTodoToShow(s);
  }, []);

  const handleDeleteTodo = useCallback((id: string) => {
    setTodos(prev => prev.filter(todo => todo.id !== id));
  }, []);

  const removeAllTodosThatAreComplete = useCallback(() => {
    setTodos(prev => prev.filter(todo => !todo.complete));
  }, []);

  const markAllToggle = useCallback(() => {
    setTodos(prev =>
      prev.map(todo => ({ ...todo, complete: toggleAllComplete }))
    );
    setToggleAllComplete(v => !v);
  }, [toggleAllComplete]);

  const filteredTodos = useMemo(() => {
    if (todoToShow === "all") return todos;
    if (todoToShow === "active") return todos.filter(todo => !todo.complete);
    return todos.filter(todo => todo.complete);
  }, [todoToShow, todos]);

  const activeCount = useMemo(
    () => todos.filter(todo => !todo.complete).length,
    [todos]
  );

  const hasComplete = useMemo(() => todos.some(todo => todo.complete), [todos]);

  return (
    <div className="dashboard-todo-list d-flex flex-column flex-grow-1 min-height-0">
      <div className="d-flex justify-content-between align-items-center mb-2 flex-shrink-0">
        <span className="text-small text-muted mb-0">
          Remaining tasks: <strong>{activeCount}</strong>
        </span>
        <Badge color="secondary" pill className="dashboard-task-count-badge">
          {todos.length}
        </Badge>
      </div>
      <div className="dashboard-tasks flex-grow-1 min-height-0">
        <PerfectScrollbar option={SCROLL_OPTIONS}>
          <div className="pe-1 pb-1">
            <TodoForm onSubmit={addTodo} />
            {filteredTodos.map(todo => (
              <Todo
                key={todo.id}
                toggleComplete={() => toggleComplete(todo.id)}
                onDelete={() => handleDeleteTodo(todo.id)}
                todo={todo}
              />
            ))}
          </div>
        </PerfectScrollbar>
      </div>
      <CardFooter className="flex-shrink-0 border-0 bg-transparent px-0 pt-3 mt-auto">
        <div className="dashboard-todo-footer d-flex flex-wrap gap-2 align-items-center">
          <span
            className="todobtn"
            role="button"
            tabIndex={0}
            onClick={() => updateTodoToShow("all")}
            onKeyDown={e =>
              e.key === "Enter" && updateTodoToShow("all")
            }
          >
            All
          </span>
          <span
            className="todobtn"
            role="button"
            tabIndex={0}
            onClick={() => updateTodoToShow("active")}
            onKeyDown={e =>
              e.key === "Enter" && updateTodoToShow("active")
            }
          >
            Active
          </span>
          <span
            className="todobtn"
            role="button"
            tabIndex={0}
            onClick={() => updateTodoToShow("complete")}
            onKeyDown={e =>
              e.key === "Enter" && updateTodoToShow("complete")
            }
          >
            Complete
          </span>
          <span
            className="todobtn"
            role="button"
            tabIndex={0}
            onClick={markAllToggle}
            onKeyDown={e => {
              if (e.key === "Enter") markAllToggle();
            }}
          >
            Mark all
          </span>
          {hasComplete ? (
            <span
              className="todobtn"
              role="button"
              tabIndex={0}
              onClick={removeAllTodosThatAreComplete}
              onKeyDown={e => {
                if (e.key === "Enter") removeAllTodosThatAreComplete();
              }}
            >
              Remove completed
            </span>
          ) : null}
        </div>
      </CardFooter>
    </div>
  );
}
